import '@fontsource-variable/inter';
import './style.css';
import { initAnalytics, sendNow, track } from './analytics';
import { COLUMNS, birthYear, compare, officeLabel, partyLabel, type Cell } from './compare';
import { dailyAnswer, dateKey, dayNumber, msUntilNextDay, randomAnswer } from './daily';
import { credits, getPolitician, photoUrl, politicians } from './data';
import { h, icon } from './dom';
import { drawPixelated, loadImage } from './pixel';
import { createSearch } from './search';
import { REPORT_CATEGORIES, reportEvent } from './report';
import { pokusy, share, shareText } from './share';
import {
  currentStreak,
  loadDay,
  loadFlag,
  loadStats,
  recordWin,
  saveDay,
  saveFlag,
  type DayState,
} from './storage';
import type { Mode, Politician } from './types';

interface Session {
  practice: boolean;
  target: Politician;
  state: DayState;
  /** Guess to animate on the next render. */
  fresh: string | null;
}

const HINT_PHOTO_AT = 4;
const HINT_INITIALS_AT = 7;
const HINT_PHOTO = 1;
const HINT_INITIALS = 2;
/** Mosaic size in the photo mode after 0, 1, 2… wrong guesses. */
const PHOTO_LEVELS = [5, 7, 9, 12, 16, 21, 28, 38, 52, 72, 100];

const MODE_NAMES: Record<Mode, string> = { classic: 'Klasika', photo: 'Fotka' };
const photoPool = politicians.filter((p) => photoUrl(p));

let today = dateKey();
let day = dayNumber(today);
let mode: Mode = location.hash === '#fotka' ? 'photo' : 'classic';
let sessions: Record<Mode, Session> = { classic: dailySession('classic'), photo: dailySession('photo') };

const main = document.getElementById('main')!;
const toastEl = document.getElementById('toast')!;
const touch = matchMedia('(pointer: coarse)').matches;

function dailySession(m: Mode): Session {
  const state = loadDay(m, day);
  state.guesses = state.guesses.filter((id) => getPolitician(id));
  return { practice: false, target: dailyAnswer(politicians, m, day), state, fresh: null };
}

function practiceSession(m: Mode): Session {
  const pool = m === 'photo' ? photoPool : politicians;
  const exclude = [sessions.classic.target.id, sessions.photo.target.id, sessions[m].target.id];
  return {
    practice: true,
    target: randomAnswer(pool, exclude),
    state: { guesses: [], won: false, hints: 0 },
    fresh: null,
  };
}

// ---------- small pieces ----------

function initials(p: Politician): string {
  return p.name
    .split(/\s+/)
    .map((w) => w[0])
    .join('');
}

function avatar(p: Politician, size: 'sm' | 'md' | 'lg' = 'sm', showPhoto = true): HTMLElement {
  const url = showPhoto ? photoUrl(p) : null;
  if (url) {
    return h('img', { class: `avatar avatar-${size}`, src: url, alt: '', loading: 'lazy', decoding: 'async' });
  }
  return h('span', { class: `avatar avatar-${size} avatar-initials`, 'aria-hidden': 'true' }, initials(p));
}

function toast(message: string): void {
  toastEl.textContent = message;
  toastEl.classList.add('show');
  clearTimeout(Number(toastEl.dataset.timer));
  toastEl.dataset.timer = String(setTimeout(() => toastEl.classList.remove('show'), 2200));
}

function celebrate(): void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const colors = ['#ffffff', '#0b4ea2', '#ee1c25', '#ffd34d'];
  const layer = h(
    'div',
    { class: 'confetti', 'aria-hidden': 'true' },
    ...Array.from({ length: 60 }, (_, i) =>
      h('i', {
        style: {
          left: `${Math.random() * 100}%`,
          background: colors[i % colors.length],
          '--delay': `${Math.random() * 0.6}s`,
          '--drift': `${(Math.random() - 0.5) * 160}px`,
          '--spin': `${(Math.random() - 0.5) * 1080}deg`,
          '--dur': `${2 + Math.random() * 1.4}s`,
        },
      }),
    ),
  );
  document.body.append(layer);
  setTimeout(() => layer.remove(), 4000);
}

function formatCountdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

function countdown(): HTMLElement {
  return h(
    'p',
    { class: 'countdown' },
    'Nový politik o ',
    h('strong', { 'data-countdown': '' }, formatCountdown(msUntilNextDay())),
  );
}

const ARROW_TEXT = { up: 'hľadaný je vyššie', down: 'hľadaný je nižšie', left: 'hľadaný je viac vľavo', right: 'hľadaný je viac vpravo' } as const;
const VERDICT_TEXT = { correct: 'zhoda', partial: 'čiastočná zhoda', wrong: 'nezhoda' } as const;

// Where long Slovak words may break in narrow cells ("-" becomes a soft hyphen).
const SYLLABLES = [
  'Bansko-bystrický',
  'Brati-slavský',
  'Tren-čiansky',
  'Nitrian-sky',
  'Pre-šovský',
  'Zahra-ničie',
  'stredo-ľavica',
  'stredo-pravica',
  'Euro-po-sla-nec',
  'Euro-po-slan-kyňa',
  'Euro-komi-sár',
  'Euro-komi-sárka',
  'Po-sla-nec',
  'Po-slan-kyňa',
  'Pred-seda',
  'Pred-sed-níčka',
  'Prezi-dent',
  'Prezi-dentka',
  'Premi-érka',
  'Minis-ter',
  'Minis-terka',
  'Nestra-ník',
  'Nestra-níčka',
  'Koa-lícia',
  'Opo-zícia',
  'poli-tiky',
  'Repu-blika',
  'Sloven-sko',
  'Demo-krati',
  'Konzer-vatívci',
  'Maďar-ská',
  'alian-cia',
];
const BREAKS = new Map(SYLLABLES.map((w) => [w.replaceAll('-', ''), w.replaceAll('-', '\u00ad')]));

function hyphenate(text: string): string {
  return text
    .split(' ')
    .map((word) => BREAKS.get(word) ?? word)
    .join(' ');
}

const REGION_SHORT: Record<string, string> = {
  Bratislavský: 'BA',
  Trnavský: 'TT',
  Trenčiansky: 'TN',
  Nitriansky: 'NR',
  Žilinský: 'ZA',
  Banskobystrický: 'BB',
  Prešovský: 'PO',
  Košický: 'KE',
  Zahraničie: 'Zahr.',
};

function cellContent(c: Cell): (HTMLElement | null)[] {
  const text =
    c.key === 'region'
      ? [h('span', { class: 'long' }, hyphenate(c.text)), h('span', { class: 'short' }, REGION_SHORT[c.text] ?? c.text)]
      : [h('span', {}, hyphenate(c.text))];
  return [
    c.arrow ? h('span', { class: `arrow arrow-${c.arrow}` }, icon('arrow')) : null,
    h('span', { class: 'cell-text' }, ...text),
  ];
}

// ---------- classic mode ----------

function classicRow(guess: Politician, target: Politician, fresh: boolean): HTMLElement {
  const cells = compare(guess, target);
  return h(
    'div',
    { class: `row${fresh ? ' fresh' : ''}`, role: 'row' },
    h('div', { class: 'cell who', role: 'cell', title: guess.name }, avatar(guess, 'md'), h('span', { class: 'who-name' }, guess.name)),
    ...cells.map((c, i) =>
      h(
        'div',
        {
          class: `cell ${c.verdict} key-${c.key}`,
          role: 'cell',
          style: { '--i': i + 1 },
          'aria-label': `${COLUMNS[i].label}: ${c.text} (${VERDICT_TEXT[c.verdict]}${c.arrow ? `, ${ARROW_TEXT[c.arrow]}` : ''})`,
        },
        ...cellContent(c),
      ),
    ),
  );
}

function classicGrid(s: Session, guesses: Politician[]): HTMLElement {
  return h(
    'div',
    { class: 'grid-wrap' },
    h(
      'div',
      { class: 'grid', role: 'table', 'aria-label': 'Tvoje pokusy' },
      h(
        'div',
        { class: 'row head', role: 'row' },
        h('div', { class: 'hcell who-head', role: 'columnheader' }, 'Politik'),
        ...COLUMNS.map((c) =>
          h(
            'div',
            { class: 'hcell', role: 'columnheader', title: c.help },
            h('span', { class: 'long' }, c.label),
            h('span', { class: 'short', 'aria-hidden': 'true' }, c.short),
          ),
        ),
      ),
      ...guesses
        .slice()
        .reverse()
        .map((g) => classicRow(g, s.target, g.id === s.fresh)),
    ),
  );
}

function legend(): HTMLElement {
  return h(
    'div',
    { class: 'legend' },
    h('span', {}, h('i', { class: 'swatch correct' }), 'zhoda'),
    h('span', {}, h('i', { class: 'swatch partial' }), 'čiastočne'),
    h('span', {}, h('i', { class: 'swatch wrong' }), 'nezhoda'),
    h('span', {}, '↑↓←→ smer k hľadanému'),
  );
}

function hints(s: Session, count: number): HTMLElement | null {
  if (s.state.won) return null;
  const photo = photoUrl(s.target);
  const reveal = (flag: number) => () => {
    track('hint_revealed', { mode, practice: s.practice, day, hint: flag === HINT_PHOTO ? 'photo' : 'initials', guesses: count });
    s.state.hints |= flag;
    persist(s);
    render();
  };
  const hintButton = (flag: number, at: number, label: string) => {
    const left = at - count;
    const open = (s.state.hints & flag) !== 0;
    if (open) return null;
    return h(
      'button',
      { class: 'hint-btn', type: 'button', disabled: left > 0, onClick: reveal(flag) },
      icon('bulb'),
      left > 0 ? `${label} o ${pokusy(left)}` : label,
    );
  };

  const shown: HTMLElement[] = [];
  if (s.state.hints & HINT_PHOTO) {
    if (photo) {
      const canvas = h('canvas', { class: 'hint-canvas', width: 240, height: 240, 'aria-label': 'Rozmazaná fotka' });
      loadImage(photo).then((img) => drawPixelated(canvas, img, 12));
      shown.push(h('div', { class: 'hint-shown' }, canvas));
    } else {
      shown.push(h('div', { class: 'hint-shown' }, 'Rodisko: ', h('strong', {}, s.target.birthplace)));
    }
  }
  if (s.state.hints & HINT_INITIALS) {
    shown.push(h('div', { class: 'hint-shown' }, 'Iniciály: ', h('strong', { class: 'initials' }, initials(s.target).split('').join('. ') + '.')));
  }

  return h(
    'div',
    { class: 'hints' },
    h(
      'div',
      { class: 'hint-buttons' },
      hintButton(HINT_PHOTO, HINT_PHOTO_AT, photo ? 'Rozmazaná fotka' : 'Rodisko'),
      hintButton(HINT_INITIALS, HINT_INITIALS_AT, 'Iniciály'),
    ),
    shown.length ? h('div', { class: 'hint-row' }, ...shown) : null,
  );
}

function classicView(s: Session, guesses: Politician[]): HTMLElement {
  return h(
    'div',
    { class: 'view' },
    h(
      'section',
      { class: 'card intro' },
      h('h2', {}, s.practice ? 'Tréning: uhádni politika' : 'Uhádni dnešného slovenského politika'),
      h('p', { class: 'muted' }, 'Napíš meno a sleduj farby – prezradia, v čom sa tvoj tip zhoduje.'),
      hints(s, guesses.length),
    ),
    s.state.won ? winCard(s, guesses) : searchFor(s),
    guesses.length ? classicGrid(s, guesses) : null,
    guesses.length ? legend() : null,
  );
}

// ---------- photo mode ----------

function photoView(s: Session, guesses: Politician[]): HTMLElement {
  const wrong = guesses.filter((g) => g.id !== s.target.id).length;
  const blocks = s.state.won ? null : PHOTO_LEVELS[Math.min(wrong, PHOTO_LEVELS.length - 1)];
  const canvas = h('canvas', { class: 'photo-canvas', width: 480, height: 480, 'aria-label': 'Rozpixelovaná fotka politika' });
  const url = photoUrl(s.target);
  if (url) loadImage(url).then((img) => drawPixelated(canvas, img, blocks));

  return h(
    'div',
    { class: 'view' },
    h(
      'section',
      { class: 'card intro' },
      h('h2', {}, s.practice ? 'Tréning: kto je na fotke?' : 'Kto je na dnešnej fotke?'),
      h('div', { class: `photo-frame${s.state.won ? ' revealed' : ''}` }, canvas),
      h(
        'p',
        { class: 'muted' },
        s.state.won ? 'Fotka odhalená.' : 'Každý nesprávny pokus fotku trochu zaostrí.',
      ),
    ),
    s.state.won ? winCard(s, guesses) : searchFor(s),
    guesses.length
      ? h(
          'ul',
          { class: 'photo-guesses' },
          ...guesses
            .slice()
            .reverse()
            .map((g) => {
              const ok = g.id === s.target.id;
              return h(
                'li',
                { class: `pguess ${ok ? 'correct' : 'wrong'}${g.id === s.fresh ? ' fresh' : ''}` },
                avatar(g, 'md'),
                h('span', { class: 'pguess-name' }, g.name),
                h('span', { class: 'pguess-mark', title: ok ? 'správne' : 'nesprávne' }, icon(ok ? 'check' : 'cross')),
              );
            }),
        )
      : null,
  );
}

// ---------- shared ----------

function searchFor(s: Session): HTMLElement {
  const search = createSearch({
    options: () => (mode === 'photo' ? photoPool : politicians).filter((p) => !s.state.guesses.includes(p.id)),
    onPick: (p) => guess(s, p),
    // In the photo mode the thumbnails would give the answer away.
    avatar: (p) => avatar(p, 'sm', mode === 'classic'),
    subtitle: (p) => `${partyLabel(p)} · ${officeLabel(p)}`,
  });
  return h('div', { class: 'search-wrap' }, search, s.state.guesses.length ? h('p', { class: 'count' }, `Pokusy: ${s.state.guesses.length}`) : null);
}

function winCard(s: Session, guesses: Politician[]): HTMLElement {
  const t = s.target;
  const age = t.died ? null : new Date().getFullYear() - birthYear(t);
  const other: Mode = mode === 'classic' ? 'photo' : 'classic';
  const otherDone = sessions[other].state.won && !sessions[other].practice;

  const onShare = async () => {
    const result = await share(shareText(mode, day, guesses, t));
    track('result_shared', { mode, day, guesses: guesses.length, method: result });
    if (result === 'copied') toast('Výsledok skopírovaný – vlož ho kamarátom.');
    else if (result === 'failed') toast('Kopírovanie nevyšlo.');
  };

  return h(
    'section',
    // Fresh classic win: wait for the row to flip before showing the answer.
    { class: `card win${s.fresh === t.id && mode === 'classic' ? ' delayed' : ''}`, 'aria-live': 'polite' },
    avatar(t, 'lg'),
    h('p', { class: 'win-kicker' }, s.practice ? 'Tréning zvládnutý!' : 'Správne! 🎉'),
    h('h2', { class: 'win-name' }, t.name),
    h(
      'p',
      { class: 'win-facts' },
      `${partyLabel(t)} · ${officeLabel(t)} · nar. ${birthYear(t)} (${t.birthplace})${age ? ` · ${age} r.` : ''}`,
    ),
    h('p', { class: 'win-count' }, `Uhádnuté na ${pokusy(guesses.length)}.`),
    h(
      'div',
      { class: 'win-actions' },
      s.practice
        ? h('button', { class: 'btn primary', type: 'button', onClick: () => startPractice() }, icon('dice'), 'Ďalší náhodný')
        : h('button', { class: 'btn primary', type: 'button', onClick: onShare }, icon('share'), 'Zdieľať'),
      !otherDone
        ? h('button', { class: 'btn', type: 'button', onClick: () => switchMode(other, true) }, icon(other === 'photo' ? 'camera' : 'grid'), `Skús ${MODE_NAMES[other]}`)
        : null,
      s.practice
        ? h('button', { class: 'btn', type: 'button', onClick: () => backToDaily() }, 'Späť na dennú hru')
        : h('button', { class: 'btn', type: 'button', onClick: () => startPractice() }, icon('dice'), 'Tréning'),
    ),
    s.practice ? null : countdown(),
    h('button', { class: 'link small', type: 'button', onClick: () => showReport(t) }, 'Nesedí niečo? Nahlás chybu'),
  );
}

function modeTabs(): HTMLElement {
  const tab = (m: Mode, ic: 'grid' | 'camera') => {
    const done = sessions[m].state.won && !sessions[m].practice;
    return h(
      'button',
      {
        class: `tab${mode === m ? ' active' : ''}`,
        type: 'button',
        role: 'tab',
        'aria-selected': String(mode === m),
        onClick: () => switchMode(m),
      },
      icon(ic),
      MODE_NAMES[m],
      done ? h('span', { class: 'tab-done', title: 'Dnes hotovo' }, icon('check')) : null,
    );
  };
  return h(
    'nav',
    { class: 'tabs', role: 'tablist' },
    tab('classic', 'grid'),
    tab('photo', 'camera'),
    h('span', { class: 'day-label' }, `#${day}`),
  );
}

function yesterday(): HTMLElement | null {
  if (day <= 1 || sessions[mode].practice) return null;
  const p = dailyAnswer(politicians, mode, day - 1);
  return h('p', { class: 'yesterday' }, `Včerajší politik: `, h('strong', {}, p.name));
}

function practiceBanner(): HTMLElement | null {
  if (!sessions[mode].practice) return null;
  return h(
    'div',
    { class: 'practice-banner' },
    h('span', {}, icon('dice'), ' Tréningový režim – nepočíta sa do štatistík.'),
    h('button', { class: 'link', type: 'button', onClick: () => backToDaily() }, 'Späť na dennú hru'),
  );
}

// ---------- state changes ----------

function persist(s: Session): void {
  if (!s.practice) saveDay(mode, day, s.state);
}

function guess(s: Session, p: Politician): void {
  if (s.state.won || s.state.guesses.includes(p.id)) return;
  s.state.guesses.push(p.id);
  s.fresh = p.id;
  const correct = p.id === s.target.id;
  const n = s.state.guesses.length;
  track('guess_made', { mode, practice: s.practice, day, guess_number: n, correct, politician: p.id });
  if (correct) {
    s.state.won = true;
    if (!s.practice) recordWin(mode, day, n);
    track('game_won', { mode, practice: s.practice, day, guesses: n, hints: s.state.hints, answer: s.target.id });
  }
  persist(s);
  render();
  if (s.state.won) {
    // Let the last row flip before celebrating.
    setTimeout(() => {
      celebrate();
      main.querySelector('.win')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, mode === 'classic' ? 1600 : 300);
  } else if (!touch) {
    main.querySelector<HTMLInputElement>('.search-input')?.focus();
  }
}

function switchMode(m: Mode, scroll = false): void {
  if (m !== mode) track('mode_switched', { to: m });
  mode = m;
  history.replaceState(null, '', m === 'photo' ? '#fotka' : location.pathname + location.search);
  render();
  if (scroll) window.scrollTo({ top: 0, behavior: 'smooth' });
}

function startPractice(): void {
  track('practice_started', { mode });
  sessions[mode] = practiceSession(mode);
  render();
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function backToDaily(): void {
  sessions[mode] = dailySession(mode);
  render();
}

function render(): void {
  const s = sessions[mode];
  const guesses = s.state.guesses.map((id) => getPolitician(id)!).filter(Boolean);
  main.replaceChildren(
    modeTabs(),
    practiceBanner() ?? '',
    mode === 'classic' ? classicView(s, guesses) : photoView(s, guesses),
    yesterday() ?? '',
  );
  // Only animate a row once.
  s.fresh = null;
}

// ---------- dialogs ----------

function dialog(id: string, title: string, ...content: (HTMLElement | string | null)[]): HTMLDialogElement {
  document.getElementById(id)?.remove();
  const d = h(
    'dialog',
    { id, class: 'modal', 'aria-label': title },
    h(
      'div',
      { class: 'modal-inner' },
      h('header', { class: 'modal-head' }, h('h2', {}, title), h('button', { class: 'icon-btn', type: 'button', 'aria-label': 'Zavrieť', onClick: () => d.close() }, icon('close'))),
      h('div', { class: 'modal-body' }, ...content),
    ),
  );
  d.addEventListener('click', (e) => {
    if (e.target === d) d.close();
  });
  d.addEventListener('close', () => d.remove());
  document.body.append(d);
  d.showModal();
  return d;
}

function showHelp(): void {
  dialog(
    'help',
    'Ako hrať',
    h('p', {}, 'Každý deň je tu nový slovenský politik. Uhádni, kto to je – pokusov máš koľko chceš. Nový politik je vždy o polnoci.'),
    h('h3', {}, icon('grid'), ' Klasika'),
    h('p', {}, 'Napíš meno ľubovoľného politika. Ukáže sa, v čom sa zhoduje s hľadaným:'),
    h(
      'ul',
      { class: 'help-legend' },
      h('li', {}, h('i', { class: 'swatch correct' }), h('span', {}, h('strong', {}, 'Zelená'), ' – zhoda.')),
      h('li', {}, h('i', { class: 'swatch partial' }), h('span', {}, h('strong', {}, 'Oranžová'), ' – čiastočná zhoda (vysvetlenie nižšie).')),
      h('li', {}, h('i', { class: 'swatch wrong' }), h('span', {}, h('strong', {}, 'Červená'), ' – nezhoda.')),
      h('li', {}, h('i', { class: 'swatch arrow-swatch' }, '↑'), h('span', {}, 'Šípky ukazujú smer: ↑↓ pri roku narodenia a funkcii, ←→ pri orientácii (vľavo/vpravo).')),
    ),
    h('dl', { class: 'help-cols' }, ...COLUMNS.flatMap((c) => [h('dt', {}, c.label), h('dd', {}, c.help)])),
    h('p', {}, 'Po 4 pokusoch si môžeš odomknúť rozmazanú fotku, po 7 iniciály.'),
    h('h3', {}, icon('camera'), ' Fotka'),
    h('p', {}, 'Uhádni politika podľa rozpixelovanej fotky. Každý nesprávny pokus ju trochu zaostrí.'),
    h('h3', {}, 'Súkromie'),
    h(
      'p',
      { class: 'muted' },
      'Hra nepoužíva cookies. Tvoje pokusy, výsledky a séria sa ukladajú len v tvojom prehliadači. Anonymné štatistiky (koľko ľudí hrá, koľko pokusov treba) a hlásenia chýb posielame do PostHogu bez cookies a bez ukladania IP adries.',
    ),
    h('h3', {}, 'Pozor'),
    h(
      'p',
      { class: 'muted' },
      'Údaje sú zjednodušené (stav k októbru 2026). Orientácia vychádza z politického zaradenia strán na Wikipédii, nie z nášho názoru. Je to neoficiálna hra pre zábavu – ak nájdeš chybu, daj vedieť cez „Nahlás chybu“ dole na stránke.',
    ),
  );
}

function showStats(which: Mode = mode): void {
  const stats = loadStats(which);
  const streak = currentStreak(stats, day);
  const avg = stats.wins ? (stats.totalGuesses / stats.wins).toFixed(1).replace('.', ',') : '–';
  const max = Math.max(1, ...Object.values(stats.dist));
  const tile = (value: string | number, label: string) => h('div', { class: 'stat' }, h('strong', {}, String(value)), h('span', {}, label));

  dialog(
    'stats',
    'Štatistiky',
    h(
      'div',
      { class: 'tabs small' },
      ...(['classic', 'photo'] as Mode[]).map((m) =>
        h('button', { class: `tab${m === which ? ' active' : ''}`, type: 'button', onClick: () => showStats(m) }, MODE_NAMES[m]),
      ),
    ),
    h('div', { class: 'stat-tiles' }, tile(stats.wins, 'uhádnutých dní'), tile(streak, 'aktuálna séria'), tile(stats.maxStreak, 'najdlhšia séria'), tile(avg, 'priemer pokusov')),
    h('h3', {}, 'Počet pokusov'),
    h(
      'div',
      { class: 'dist' },
      ...Array.from({ length: 10 }, (_, i) => {
        const n = stats.dist[String(i + 1)] ?? 0;
        return h(
          'div',
          { class: 'dist-row' },
          h('span', { class: 'dist-label' }, i === 9 ? '10+' : String(i + 1)),
          h('span', { class: `dist-bar${n ? ' filled' : ''}`, style: { width: `${Math.max(6, (n / max) * 100)}%` } }, String(n)),
        );
      }),
    ),
  );
}

function showCredits(): void {
  const rows = politicians
    .filter((p) => credits.has(p.id))
    .map((p) => {
      const c = credits.get(p.id)!;
      return h(
        'li',
        {},
        avatar(p, 'sm'),
        h(
          'span',
          {},
          h('strong', {}, p.name),
          h('br'),
          h('span', { class: 'muted' }, `${c.author} · `),
          c.licenseUrl ? h('a', { href: c.licenseUrl, target: '_blank', rel: 'noopener' }, c.license) : c.license,
          ' · ',
          h('a', { href: c.sourceUrl, target: '_blank', rel: 'noopener' }, 'zdroj'),
        ),
      );
    });
  dialog(
    'credits',
    'Fotky a zdroje',
    h(
      'p',
      {},
      'Fotografie pochádzajú z Wikimedia Commons a sú orezané. Údaje o politikoch sú z Wikipédie a Wikidata. Ďakujeme autorom:',
    ),
    h('ul', { class: 'credits' }, ...rows),
  );
}

function showReport(about: Politician | null = null): void {
  track('dialog_opened', { dialog: 'report' });
  const who = h(
    'select',
    { class: 'field', name: 'who' },
    h('option', { value: '' }, '— nevybraný —'),
    ...politicians.map((p) => h('option', { value: p.id, selected: p.id === about?.id }, p.name)),
  );
  const text = h('textarea', {
    class: 'field',
    name: 'text',
    rows: 4,
    maxlength: 1000,
    placeholder: 'Napr. „Strana má byť … (zdroj: odkaz)“',
  });
  const error = h('p', { class: 'form-error', role: 'alert' });
  const submit = h('button', { class: 'btn primary', type: 'submit' }, 'Odoslať');

  const form = h(
    'form',
    { class: 'report-form', novalidate: true },
    h('p', { class: 'muted' }, 'Vidíš v údajoch chybu alebo niečo v hre nefunguje? Daj vedieť, opravíme to.'),
    h('label', { class: 'field-label' }, h('span', {}, 'O ktorého politika ide? ', h('span', { class: 'muted' }, '(nepovinné)')), who),
    h(
      'fieldset',
      { class: 'chips' },
      h('legend', { class: 'field-label' }, 'Čo nesedí?'),
      ...REPORT_CATEGORIES.map((c) => h('label', { class: 'chip' }, h('input', { type: 'radio', name: 'category', value: c }), h('span', {}, c))),
    ),
    h('label', { class: 'field-label' }, 'Popíš chybu', text),
    error,
    h('div', { class: 'form-actions' }, submit),
  );

  form.addEventListener('input', () => {
    error.textContent = '';
  });
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const category = form.querySelector<HTMLInputElement>('input[name=category]:checked')?.value as (typeof REPORT_CATEGORIES)[number] | undefined;
    const body = text.value.trim();
    if (!category) {
      error.textContent = 'Vyber, čo nesedí.';
      return;
    }
    if (body.length < 5) {
      error.textContent = 'Napíš aspoň pár slov.';
      text.focus();
      return;
    }
    error.textContent = '';
    submit.disabled = true;
    submit.textContent = 'Odosielam…';
    const s = sessions[mode];
    const ok = await sendNow(
      'survey sent',
      reportEvent({ politician: getPolitician(who.value) ?? null, category, text: body, mode, day, practice: s.practice }),
    );
    if (ok) {
      d.close();
      toast('Ďakujeme! Pozrieme sa na to.');
    } else {
      error.textContent = 'Hlásenie sa nepodarilo odoslať. Asi ho blokuje blokovač reklám – vypni ho pre túto stránku a skús znova.';
      submit.disabled = false;
      submit.textContent = 'Odoslať';
    }
  });

  const d = dialog('report', 'Nahlás chybu', form);
}

// ---------- boot ----------

const opener = (dialog: string, show: () => void) => () => {
  track('dialog_opened', { dialog });
  show();
};
document.getElementById('btn-help')!.addEventListener('click', opener('help', showHelp));
document.getElementById('btn-stats')!.addEventListener('click', opener('stats', () => showStats()));
document.getElementById('btn-credits')!.addEventListener('click', opener('credits', showCredits));
document.getElementById('btn-report')!.addEventListener('click', () => showReport());
window.addEventListener('hashchange', () => {
  const m: Mode = location.hash === '#fotka' ? 'photo' : 'classic';
  if (m !== mode) switchMode(m);
});

setInterval(() => {
  if (dateKey() !== today) {
    today = dateKey();
    day = dayNumber(today);
    sessions = { classic: dailySession('classic'), photo: dailySession('photo') };
    render();
    return;
  }
  const el = main.querySelector('[data-countdown]');
  if (el) el.textContent = formatCountdown(msUntilNextDay());
}, 1000);

render();
initAnalytics();
if (!loadFlag('seen-help')) {
  saveFlag('seen-help');
  track('first_visit');
  showHelp();
}
